import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  type SVGProps,
} from 'react';
import classnames from 'classnames';
import { useExperience } from '@unisporkal/experiences';
import { useTranslations } from '@unisporkal/localization';
import { useSearchBar, useSpectrum } from '../../../hooks';
import IconCheckmark from '../../../icons/shared/checkmark.svg?icon';
import IconArrow from '../../../icons/shared/arrow.svg?icon';
import iconPhotos from '../../../icons/istock/photos.svg?icon';
import iconImages from '../../../icons/istock/images.svg?icon';
import iconIllustrations from '../../../icons/istock/illustrations.svg?icon';
import iconVectors from '../../../icons/istock/vectors.svg?icon';
import iconVideo from '../../../icons/istock/video.svg?icon';
import iconAllFiles from '../../../icons/istock/allfiles.svg?icon';
import iconVideoTemplates from '../../../icons/istock/video-templates.svg?icon';

interface MediaOption {
  searchParameters: {
    assettype: string;
    mediatype: string | null;
    assetfiletype: string | null;
  };
  icon: React.ComponentType<SVGProps<SVGSVGElement>>;
  trackingEvent: string;
}

const OPTIONS = {
  imagefilm: {
    searchParameters: {
      assettype: 'image,film',
      mediatype: null,
      assetfiletype: null,
    },
    icon: iconAllFiles,
    trackingEvent: 'images-videos',
  },
  images: {
    searchParameters: {
      assettype: 'image',
      mediatype: null,
      assetfiletype: null,
    },
    icon: iconImages,
    trackingEvent: 'images',
  },
  photos: {
    searchParameters: {
      assettype: 'image',
      mediatype: 'photography',
      assetfiletype: null,
    },
    icon: iconPhotos,
    trackingEvent: 'images-photos',
  },
  illustrations: {
    searchParameters: {
      assettype: 'image',
      mediatype: 'illustration',
      assetfiletype: null,
    },
    icon: iconIllustrations,
    trackingEvent: 'images-illustrations',
  },
  vectors: {
    searchParameters: {
      assettype: 'image',
      mediatype: 'illustration',
      assetfiletype: 'eps',
    },
    icon: iconVectors,
    trackingEvent: 'images-vectors',
  },
  video: {
    searchParameters: {
      assettype: 'film',
      mediatype: null,
      assetfiletype: null,
    },
    icon: iconVideo,
    trackingEvent: 'video',
  },
} satisfies Record<string, MediaOption>;

// Enter, not keydown-activate, which would also add Space.
const OPEN_ACTION =
  'click: open_search_asset_family_options; keydown[Enter]: open_search_asset_family_options';
const SELECT_ACTION =
  'click: select_search_asset_family_option; keydown[Enter]: select_search_asset_family_option';

interface BaseMediaFilterProps {
  styles?: Record<string, string>;
}

const BaseMediaFilter = ({ styles = {} }: BaseMediaFilterProps) => {
  const t = useTranslations();
  const searchBar = useSearchBar();
  const spectrum = useSpectrum();
  const [dropdownClosed, setDropdownClosed] = useState(true);
  const baseMediaContainerRef = useRef<HTMLElement>(null);
  const videoTemplateExpIsActive =
    useExperience('VideoTemplatesSRP').isActive();
  const videotemplates = 'videotemplates';
  const assetTypeOptions: Record<string, MediaOption> = { ...OPTIONS };

  if (videoTemplateExpIsActive) {
    assetTypeOptions[videotemplates] = {
      searchParameters: {
        assettype: 'videotemplate',
        mediatype: null,
        assetfiletype: null,
      },
      icon: iconVideoTemplates,
      trackingEvent: 'video-templates',
    };
  }

  useEffect(() => {
    if (
      baseMediaContainerRef.current &&
      baseMediaContainerRef.current.getBoundingClientRect().width !== 0
    ) {
      const autoSuggestContainerWidth =
        baseMediaContainerRef.current.getBoundingClientRect().width;
      searchBar.setAutoSuggestContainerWidth(autoSuggestContainerWidth);
    }
  }, [baseMediaContainerRef.current]);

  const selectedImageCategory = (): string => {
    if (searchBar.vectorSearch()) {
      return 'vectors';
    }
    if (searchBar.photographySearch()) {
      return 'photos';
    }
    if (searchBar.illustrationSearch()) {
      return 'illustrations';
    }

    return 'images';
  };

  const selectedMedia = (): string => {
    if (searchBar.imageAndFilmSearch()) {
      return 'imagefilm';
    }
    if (searchBar.filmSearch()) {
      return 'video';
    }
    if (searchBar.videoTemplateSearch()) {
      return 'videotemplates';
    }

    return selectedImageCategory();
  };

  const toggleDropdown = () => {
    setDropdownClosed(!dropdownClosed);
  };

  const toggleKeyboardDropdown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      toggleDropdown();
    }
  };

  const handleMouseLeave = () => {
    if (dropdownClosed) {
      return;
    }
    setDropdownClosed(true);
  };

  const handleClick = (mediaType: string) => {
    const option = assetTypeOptions[mediaType];
    if (!option) return;
    const { searchParameters } = option;
    toggleDropdown();
    searchBar.updateSearchBarSearchParameters(searchParameters);
    searchBar.updateSearchBar({ inFocus: false });
    spectrum.sendItemSelectedSignal('media_type_change', mediaType);

    if (!searchBar.options.performSearch) {
      searchBar.setStatusToUpdated();
    }
  };

  const handleKeyDown = (mediaType: string) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleClick(mediaType);
    }
  };

  const mediaLabel = (mediaType: string) => {
    if (mediaType === 'imagefilm') {
      return videoTemplateExpIsActive
        ? t('all_images_ampersand_video')
        : t('all_content');
    }
    if (mediaType === 'videotemplates') {
      return t('video_templates');
    }

    return t(mediaType);
  };

  const mediaDropdownItems = () =>
    Object.entries(assetTypeOptions).map(([mediaType, config]) => {
      const labelOrder = [
        <config.icon
          key="iconLabel"
          className={styles.labelIcon ?? ''}
        />,
        <span key="mediaLabel">{mediaLabel(mediaType)}</span>,
        selectedMedia() === mediaType && (
          <IconCheckmark
            key="iconCheckmark"
            className={styles.iconCheckmark}
            viewBox="0 0 100 100"
          />
        ),
      ];
      return (
        <li
          data-testid="media-filter-option"
          data-trk-action={SELECT_ACTION}
          data-trk-action-context={`value:${config.trackingEvent}`}
          key={mediaType}
          className={styles.option}
          onClick={() => handleClick(mediaType)}
          onKeyDown={handleKeyDown(mediaType)}
          role="option"
          aria-selected={selectedMedia() === mediaType}
          aria-label={mediaType}
          tabIndex={0}
        >
          <div
            data-testid="media-filter-option"
            className={styles.holder}
          >
            <span>
              <label className={styles.dropdownLabel}>{labelOrder}</label>
            </span>
          </div>
        </li>
      );
    });

  const selectedOption = assetTypeOptions[selectedMedia()] ?? OPTIONS.imagefilm;
  const SelectedIcon = selectedOption.icon;

  const mediaOptionsStyles = useMemo(
    () =>
      classnames(styles.mediaOptions, {
        [styles.open!]: !dropdownClosed,
        [styles.imageFilmGridLabels!]: true,
        [styles.videoTemplateGridLabels!]: videoTemplateExpIsActive,
        [styles.imageFilmGridMenuHeight!]: !dropdownClosed,
        [styles.videoTemplateGridMenuHeight!]:
          videoTemplateExpIsActive && !dropdownClosed,
      }),
    [dropdownClosed, videoTemplateExpIsActive]
  );

  return (
    <section
      ref={baseMediaContainerRef}
      className={styles.container}
      onMouseLeave={handleMouseLeave}
      data-testid="section-media-filter"
      data-trk-containerid="search_bar_media_filter"
    >
      <div
        data-testid="media-filter-toggle"
        // Only while closed: this element receives the closing click too.
        data-trk-action={dropdownClosed ? OPEN_ACTION : undefined}
        data-trk-action-context="searchSelection:asset_family_options"
        role="combobox"
        aria-controls="media-filters"
        aria-expanded={!dropdownClosed}
        aria-labelledby="selected-label"
        className={styles.mainLabel}
        onClick={toggleDropdown}
        onKeyDown={toggleKeyboardDropdown}
        tabIndex={0}
      >
        <div className={styles.selectedIconGroup}>
          <SelectedIcon className={styles.selectedIcon ?? ''} />
          <span
            className={styles.selectedLabel}
            data-testid="selectedLabel"
            id="selected-label"
          >
            {mediaLabel(selectedMedia())}
          </span>
        </div>
        <IconArrow
          className={`${
            dropdownClosed ? styles.iconClosed ?? '' : styles.iconOpen ?? ''
          }`}
        />

        <div
          data-testid="media-filter-dropdown-container"
          className={mediaOptionsStyles}
        >
          <ul
            data-testid="base-media-fiter-media-dropdown"
            id="media-filters"
            role="listbox"
            aria-labelledby="selected-label"
          >
            {mediaDropdownItems()}
          </ul>
        </div>
      </div>
    </section>
  );
};

export default BaseMediaFilter;
